// frontend/src/components/p2p/OrderForm.tsx
import { useState } from 'react'
import { useAccount, useBalance } from 'wagmi'
import { Loader2, Plus } from 'lucide-react'
import { parseUnits, formatUnits } from 'viem'
import { useCreateOrder } from '../../hooks/useCreateOrder'
import { useP2PMinOrderAmount } from '../../hooks/useP2PMinOrderAmount'
import { P2POrderSide, infoForSide, infoForOppositeSide, wagmiChain } from '../../lib/p2p/tokens'
import { InfoTooltip } from './InfoTooltip'
import { OrderFlowTerminal } from './OrderFlowTerminal'
import { cn } from '../../lib/utils'

const EXPIRES_PRESETS: { label: string; hours: number }[] = [
  { label: '1h', hours: 1 },
  { label: '6h', hours: 6 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
]

const PREMIUM_MIN_PCT = -10
const PREMIUM_MAX_PCT = 10
const PREMIUM_STEP_PCT = 0.01

export function OrderForm() {
  const { address, isConnected } = useAccount()
  const { createOrder, stage, steps, error, txHash, reset } = useCreateOrder()
  const [terminalOpen, setTerminalOpen] = useState(false)

  const [side, setSide] = useState<P2POrderSide>('SELL_W2USDT')
  const [amountStr, setAmountStr] = useState('')
  const [premiumPct, setPremiumPct] = useState(0)
  const [expiresHours, setExpiresHours] = useState(24)
  const [fillMode, setFillMode] = useState<'PARTIAL_OK' | 'AON'>('PARTIAL_OK')

  const info = infoForSide(side)
  const oppositeInfo = infoForOppositeSide(side)
  const nativeSymbol = wagmiChain(info.chain).nativeCurrency.symbol
  const balance = useBalance({
    address,
    chainId: info.chainId,
    token: info.tokenAddress,
  })
  // Native gas balance on the same chain. The user signs approve +
  // createOrder; if they have zero native, neither tx can be broadcast.
  const nativeBalance = useBalance({
    address,
    chainId: info.chainId,
  })

  let amountWei = 0n
  try {
    if (amountStr) amountWei = parseUnits(amountStr, info.decimals)
  } catch {
    amountWei = 0n
  }

  const premiumBps = Math.round(premiumPct * 100)
  const { minWei, isLoading: minLoading } = useP2PMinOrderAmount(side)
  // Treat undefined balance as "not yet known" — block submit instead of
  // letting an empty wallet sign a tx that will revert on-chain.
  const balancesLoading =
    isConnected && (balance.data === undefined || nativeBalance.data === undefined)
  const insufficient = balance.data ? amountWei > balance.data.value : false
  const noGas = nativeBalance.data ? nativeBalance.data.value === 0n : false
  // El contrato revierte con AmountTooSmall cuando amount < minOrderAmount,
  // pero el revert llega a la wallet, no al dApp (WalletConnect lo enmascara
  // como "Request expired"). Por eso bloqueamos antes de firmar.
  const belowMin = minWei != null && amountWei > 0n && amountWei < minWei
  const busy =
    stage === 'building' ||
    stage === 'switching' ||
    stage === 'checkingAllowance' ||
    stage === 'approving' ||
    stage === 'creating'

  const canSubmit =
    isConnected &&
    amountWei > 0n &&
    !balancesLoading &&
    !insufficient &&
    !noGas &&
    !belowMin &&
    !busy

  // expectedAmount = amount * (10000 + premium) / 10000 in opposite-token units
  const expected = (() => {
    if (amountWei === 0n) return 0n
    const num = BigInt(10000 + premiumBps)
    return (amountWei * num) / 10000n
  })()

  async function onSubmit() {
    setTerminalOpen(true)
    const ok = await createOrder({
      side,
      amount: amountWei.toString(),
      premiumBps,
      expiresAt: Math.floor(Date.now() / 1000) + expiresHours * 3600,
      fillMode,
    })
    if (ok.ok) {
      setAmountStr('')
      setPremiumPct(0)
    }
  }

  function closeTerminal() {
    setTerminalOpen(false)
    reset()
  }

  return (
    <div className="space-y-5">
      {/* Side */}
      <Field
        label="¿Qué pones a la venta?"
        tooltip="Eliges qué token bloqueas en el escrow. Si vendes w2usdt (Uni2 chain), buscas USDT a cambio. Si vendes USDT (BSC), buscas w2usdt."
      >
        <div className="grid grid-cols-2 gap-2">
          {(['SELL_W2USDT', 'SELL_USDT'] as P2POrderSide[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSide(s)}
              className={cn(
                'rounded-lg py-2.5 px-3 text-sm border transition-all flex flex-col items-center justify-center gap-0.5',
                side === s
                  ? s === 'SELL_W2USDT'
                    ? 'bg-rose-500/15 text-rose-300 border-rose-500/40 shadow-glow-sm'
                    : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40 shadow-glow-sm'
                  : 'bg-dark-800/50 text-dark-400 border-dark-700 hover:text-white hover:border-dark-600',
              )}
            >
              <span className="font-medium">{s === 'SELL_W2USDT' ? 'w2usdt' : 'USDT'}</span>
              <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">
                {s === 'SELL_W2USDT' ? 'Uni2' : 'BSC'}
              </span>
            </button>
          ))}
        </div>
      </Field>

      {/* Amount */}
      <Field
        label={`Cantidad de ${info.tokenSymbol} que ofreces`}
        tooltip="Es la cantidad exacta que bloqueas en el escrow on-chain. Cuando alguien tome tu orden (total o parcial), liberas esa porción y recibes lo equivalente en la otra chain."
      >
        <input
          type="text"
          inputMode="decimal"
          value={amountStr}
          onChange={(e) => setAmountStr(e.target.value.replace(/[^\d.]/g, ''))}
          placeholder="0.00"
          className={cn(
            'w-full rounded-lg bg-dark-800/50 border px-3 py-2.5 font-display text-2xl font-medium focus:outline-none transition-colors',
            insufficient || belowMin
              ? 'border-rose-500/40 text-rose-400'
              : 'border-dark-700 text-white focus:border-uni-500/50',
          )}
        />
        {balance.data && (
          <div className="text-[11px] font-mono mt-1.5 flex justify-between">
            <span className="text-dark-500">
              Saldo · {formatUnits(balance.data.value, info.decimals).slice(0, 14)} {info.tokenSymbol}
            </span>
            {balance.data.value > 0n && (
              <button
                type="button"
                onClick={() => setAmountStr(formatUnits(balance.data!.value, info.decimals))}
                className="text-uni-400 hover:text-uni-300 px-1.5 py-0.5 rounded border border-uni-500/30 hover:border-uni-500/50"
              >
                MAX
              </button>
            )}
          </div>
        )}
        {minWei != null && minWei > 0n && (
          <div className={cn(
            'text-[11px] font-mono mt-1',
            belowMin ? 'text-rose-400' : 'text-dark-500',
          )}>
            Mínimo · {formatUnits(minWei, info.decimals)} {info.tokenSymbol}
          </div>
        )}
      </Field>

      {/* Premium — numeric input -10 to +10 */}
      <Field
        label="Prima / Comisión"
        tooltip={
          <>
            Es tu margen sobre la paridad 1:1.
            <br /><br />
            <strong className="text-emerald-300">+1%</strong> = quieres recibir <strong>101</strong> por cada <strong>100</strong> que das (cobras comisión).
            <br />
            <strong className="text-rose-300">−1%</strong> = aceptas <strong>99</strong> por cada <strong>100</strong> que das (das descuento, tu orden es más atractiva).
            <br />
            <strong className="text-uni-300">0%</strong> = trueque exacto sin comisión.
            <br /><br />
            Cuanto más negativa, más rápido se llenará tu orden. El contrato la cap a ±10%.
          </>
        }
      >
        <div className="flex items-center gap-2">
          <PremiumStepper onClick={() => setPremiumPct((p) => Math.max(PREMIUM_MIN_PCT, +(p - 0.1).toFixed(2)))} direction="-" />
          <div className="relative flex-1">
            <input
              type="number"
              min={PREMIUM_MIN_PCT}
              max={PREMIUM_MAX_PCT}
              step={PREMIUM_STEP_PCT}
              value={premiumPct}
              onChange={(e) => {
                const v = Number(e.target.value)
                if (Number.isFinite(v)) {
                  setPremiumPct(Math.max(PREMIUM_MIN_PCT, Math.min(PREMIUM_MAX_PCT, v)))
                }
              }}
              className={cn(
                'w-full text-center rounded-lg bg-dark-800/50 border px-3 py-2.5 font-mono text-lg focus:outline-none transition-colors',
                premiumPct > 0
                  ? 'border-emerald-500/30 text-emerald-300 focus:border-emerald-500/60'
                  : premiumPct < 0
                  ? 'border-rose-500/30 text-rose-300 focus:border-rose-500/60'
                  : 'border-dark-700 text-white focus:border-uni-500/50',
              )}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-dark-500 font-mono text-sm">%</span>
          </div>
          <PremiumStepper onClick={() => setPremiumPct((p) => Math.min(PREMIUM_MAX_PCT, +(p + 0.1).toFixed(2)))} direction="+" />
        </div>
        {/* Visual scale — also acts as a draggable slider via an invisible
            <input type="range"> overlay (gives free mouse/touch/keyboard
            interaction without restyling the native thumb). */}
        <div className="mt-2 relative h-3 group">
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 bg-gradient-to-r from-rose-500/30 via-dark-700 to-emerald-500/30 rounded-full" />
          <span
            className="absolute top-1/2 -translate-y-1/2 w-2 h-3 bg-uni-500 rounded-sm shadow-glow-sm pointer-events-none transition-[left] duration-75"
            style={{ left: `calc(${((premiumPct - PREMIUM_MIN_PCT) / (PREMIUM_MAX_PCT - PREMIUM_MIN_PCT)) * 100}% - 4px)` }}
          />
          <input
            type="range"
            min={PREMIUM_MIN_PCT}
            max={PREMIUM_MAX_PCT}
            step={PREMIUM_STEP_PCT}
            value={premiumPct}
            onChange={(e) => setPremiumPct(+(+e.target.value).toFixed(2))}
            aria-label="Prima en porcentaje"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
        <div className="flex justify-between text-[10px] font-mono text-dark-500 mt-1">
          <span>−10% descuento</span>
          <span>0%</span>
          <span>+10% comisión</span>
        </div>
        {expected > 0n && (
          <p className="text-[11px] font-mono text-dark-400 mt-2 px-2 py-1.5 rounded bg-dark-800/50 border border-dark-700">
            Esperas recibir{' '}
            <span className="text-white tabular-nums">
              {formatUnits(expected, oppositeInfo.decimals).slice(0, 12)}
            </span>{' '}
            {oppositeInfo.tokenSymbol}
          </p>
        )}
      </Field>

      {/* Expires */}
      <Field
        label="Tu orden expira en"
        tooltip="Si nadie la toma en este plazo, se cancela automáticamente y recuperas tus tokens. El cron del relayer paga el gas del expireOrder."
      >
        <div className="grid grid-cols-4 gap-1.5">
          {EXPIRES_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => setExpiresHours(p.hours)}
              className={cn(
                'rounded-lg py-1.5 text-xs font-mono border transition-all',
                expiresHours === p.hours
                  ? 'bg-uni-500/15 text-uni-400 border-uni-500/40'
                  : 'bg-dark-800/50 text-dark-400 border-dark-700 hover:text-white',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </Field>

      {/* Fill mode */}
      <Field
        label="Modo de llenado"
        tooltip={
          <>
            <strong className="text-uni-300">Parcial OK</strong>: otros pueden tomar pedacitos de tu orden. Más probable que se ejecute, pero llega en partes.
            <br /><br />
            <strong className="text-uni-300">Todo o nada (AON)</strong>: solo se ejecuta si alguien la toma entera. Útil para órdenes grandes que no quieres fraccionar.
          </>
        }
      >
        <div className="grid grid-cols-2 gap-2">
          {(['PARTIAL_OK', 'AON'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setFillMode(m)}
              className={cn(
                'rounded-lg py-2 text-xs border transition-all',
                fillMode === m
                  ? 'bg-uni-500/15 text-uni-400 border-uni-500/40'
                  : 'bg-dark-800/50 text-dark-400 border-dark-700 hover:text-white',
              )}
            >
              {m === 'PARTIAL_OK' ? 'Parcial OK' : 'Todo o nada'}
            </button>
          ))}
        </div>
      </Field>

      {/* Submit */}
      <button
        type="button"
        onClick={onSubmit}
        disabled={!canSubmit}
        className={cn(
          'w-full rounded-xl py-3.5 font-medium transition-all flex items-center justify-center gap-2',
          canSubmit
            ? 'bg-gradient-to-r from-uni-600 via-uni-500 to-uni-600 text-dark-950 shadow-glow hover:shadow-glow-lg animate-glow'
            : 'bg-dark-800/60 text-dark-500 cursor-not-allowed',
        )}
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="font-mono text-xs tracking-wide uppercase">
              {stage === 'building' && 'preparando'}
              {stage === 'switching' && 'cambiando red'}
              {stage === 'checkingAllowance' && 'verificando aprobación'}
              {stage === 'approving' && 'aprobando token'}
              {stage === 'creating' && 'firmando'}
            </span>
          </>
        ) : !isConnected ? (
          <span>Conecta wallet</span>
        ) : balancesLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Cargando saldos…</span>
          </>
        ) : insufficient ? (
          <span>Saldo {info.tokenSymbol} insuficiente</span>
        ) : noGas ? (
          <span>Te falta {nativeSymbol} para gas</span>
        ) : amountWei === 0n ? (
          <span>Ingresa una cantidad</span>
        ) : minLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Cargando mínimo…</span>
          </>
        ) : belowMin ? (
          <span>
            Mínimo {formatUnits(minWei!, info.decimals)} {info.tokenSymbol}
          </span>
        ) : (
          <>
            <Plus className="w-4 h-4" />
            <span>Publicar orden</span>
          </>
        )}
      </button>

      <OrderFlowTerminal
        open={terminalOpen}
        stage={stage}
        steps={steps}
        txHash={txHash}
        error={error}
        onClose={closeTerminal}
        title="Creando orden"
      />
    </div>
  )
}

function Field({
  label,
  tooltip,
  children,
}: {
  label: string
  tooltip: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="text-[11px] font-mono text-dark-400 uppercase tracking-wider flex items-center mb-1.5">
        {label}
        <InfoTooltip>{tooltip}</InfoTooltip>
      </label>
      {children}
    </div>
  )
}

function PremiumStepper({ onClick, direction }: { onClick: () => void; direction: '+' | '-' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-10 h-10 sm:w-9 sm:h-9 shrink-0 rounded-lg border border-dark-700 bg-dark-800/50 text-dark-300 hover:text-white hover:border-dark-600 transition-colors flex items-center justify-center font-mono text-base"
      aria-label={direction === '+' ? 'Aumentar prima' : 'Disminuir prima'}
    >
      {direction === '+' ? '+' : '−'}
    </button>
  )
}
